import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Check } from 'lucide-react';
import {
  procurementApi, PROCUREMENT_STAGES, STAGE_LABELS, STATUS_LABELS, CATEGORY_LABELS, METHOD_LABELS, formatMoney, ProcurementDetail,
} from '../../api/procurement';
import { genericCommentApi } from '../../api/comments';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { usePermission } from '../../auth/AuthContext';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { Modal } from '../../components/Modal';
import { UserPicker } from '../../components/UserPicker';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'requisition', label: 'Requisition' },
  { key: 'sourcing', label: 'Sourcing' },
  { key: 'documents', label: 'Documents' },
  { key: 'activity', label: 'Activity' },
] as const;
type TabKey = typeof TABS[number]['key'];

/** Only real, derivable conditions - never decorative alerts (spec section 22: "actual outstanding items only"). */
function attentionItems(p: ProcurementDetail): string[] {
  const items: string[] = [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const active = !['cancelled', 'completed'].includes(p.status);
  if (p.status === 'draft' && Number(p.item_count) === 0) items.push('No line items yet - add at least one on the Requisition tab before submitting.');
  if (p.status === 'draft' && Number(p.item_count) > 0) items.push('Draft ready - submit it to start procurement.');
  if (active && p.stage === 'sourcing' && !p.procurement_method) items.push('Procurement method not selected - required before leaving Sourcing.');
  if (active && p.stage === 'sourcing' && !p.procurement_officer_id) items.push('No procurement officer assigned.');
  if (active && p.required_by_date && new Date(p.required_by_date) < today) items.push(`Required-by date (${formatDate(p.required_by_date)}) has passed.`);
  if (active && p.stage === 'bidding' && p.closing_date && new Date(p.closing_date) < today) items.push(`Bid closing date (${formatDate(p.closing_date)}) has passed - ready to move to Evaluation.`);
  return items;
}

function nextAction(p: ProcurementDetail): string {
  if (p.status === 'cancelled') return 'None - procurement cancelled';
  if (p.status === 'completed') return 'None - procurement completed';
  if (p.status === 'draft') return 'Complete the requisition and submit';
  if (p.stage === 'closeout') return 'Complete closeout';
  const next = PROCUREMENT_STAGES[PROCUREMENT_STAGES.indexOf(p.stage as typeof PROCUREMENT_STAGES[number]) + 1];
  return `Complete ${STAGE_LABELS[p.stage]} and advance to ${STAGE_LABELS[next]}`;
}

export function ProcurementWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const procurementId = Number(id);
  const canManage = usePermission('procurement.manage');
  const canAdvance = usePermission('procurement.advance');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advanceNote, setAdvanceNote] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const detailQuery = useQuery({ queryKey: ['procurement', procurementId], queryFn: () => procurementApi.get(procurementId) });
  const countsQuery = useQuery({ queryKey: ['comment-counts', 'procurement', procurementId], queryFn: () => genericCommentApi.counts('procurement', procurementId) });
  const docsQuery = useQuery({ queryKey: ['procurement-documents', procurementId], queryFn: () => genericDocumentApi.listFor('procurement', procurementId), enabled: activeTab === 'documents' });

  const draftForm = useForm<Record<string, string>>();
  const itemForm = useForm<{ description: string; specification: string; unit: string; quantity: string; estimatedUnitCost: string }>();
  const assignForm = useForm<{ procurementMethod: string; procurementOfficerId: string; closingDate: string }>();

  const refresh = () => { queryClient.invalidateQueries({ queryKey: ['procurement', procurementId] }); queryClient.invalidateQueries({ queryKey: ['procurements'] }); };
  const onErr = (fallback: string) => (err: unknown) => showError(err instanceof ApiError ? err.message : fallback);

  const saveDraft = useMutation({
    mutationFn: (v: Record<string, string>) => procurementApi.updateDraft(procurementId, {
      title: v.title, description: v.description || null, justification: v.justification || null, deliveryRequirements: v.deliveryRequirements || null,
      estimatedValue: v.estimatedValue ? Number(v.estimatedValue) : null, requiredByDate: v.requiredByDate || null,
    }),
    onSuccess: () => { showSuccess('Requisition saved.'); refresh(); }, onError: onErr('Failed to save.'),
  });
  const addItem = useMutation({
    mutationFn: (v: { description: string; specification: string; unit: string; quantity: string; estimatedUnitCost: string }) => procurementApi.addItem(procurementId, {
      description: v.description, specification: v.specification || null, unit: v.unit, quantity: Number(v.quantity), estimatedUnitCost: v.estimatedUnitCost ? Number(v.estimatedUnitCost) : null,
    }),
    onSuccess: () => { itemForm.reset(); refresh(); }, onError: onErr('Failed to add item.'),
  });
  const removeItem = useMutation({ mutationFn: (itemId: number) => procurementApi.removeItem(procurementId, itemId), onSuccess: refresh, onError: onErr('Failed to remove item.') });
  const submit = useMutation({ mutationFn: () => procurementApi.submit(procurementId), onSuccess: () => { showSuccess('Procurement request submitted.'); refresh(); }, onError: onErr('Failed to submit.') });
  const advance = useMutation({
    mutationFn: () => procurementApi.advance(procurementId, advanceNote || undefined),
    onSuccess: (r) => { showSuccess(r.data.status === 'completed' ? 'Procurement completed.' : `Moved to ${STAGE_LABELS[r.data.stage]}.`); setAdvanceOpen(false); setAdvanceNote(''); refresh(); },
    onError: onErr('Failed to advance.'),
  });
  const cancel = useMutation({
    mutationFn: () => procurementApi.cancel(procurementId, cancelReason),
    onSuccess: () => { showSuccess('Procurement cancelled.'); setCancelOpen(false); setCancelReason(''); refresh(); },
    onError: onErr('Failed to cancel.'),
  });
  const assign = useMutation({
    mutationFn: (v: { procurementMethod: string; procurementOfficerId: string; closingDate: string }) => procurementApi.assign(procurementId, {
      procurementMethod: v.procurementMethod || null, procurementOfficerId: v.procurementOfficerId ? Number(v.procurementOfficerId) : null, closingDate: v.closingDate || null,
    }),
    onSuccess: () => { showSuccess('Sourcing details saved.'); refresh(); }, onError: onErr('Failed to save sourcing details.'),
  });

  if (detailQuery.isError) return <div className="page"><div className="error-state">Failed to load this procurement.</div></div>;
  const p = detailQuery.data?.data;
  if (!p) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  const isDraft = p.status === 'draft';
  const isActive = ['submitted', 'in_progress'].includes(p.status);
  const isTerminal = ['cancelled', 'completed'].includes(p.status);
  const currentIndex = PROCUREMENT_STAGES.indexOf(p.stage as typeof PROCUREMENT_STAGES[number]);
  const countFor = (section: string) => countsQuery.data?.data.find((c) => c.section === section);
  const attention = attentionItems(p);
  const nextStageLabel = p.stage === 'closeout' ? 'Complete Procurement' : `Advance to ${STAGE_LABELS[PROCUREMENT_STAGES[currentIndex + 1]]}`;
  const itemsTotal = Number(p.items_estimated_total);

  return (
    <div className="page proc-page">
      <Link to="/procurement" className="table-link">← Procurement</Link>

      <div className="proc-header">
        <div className="proc-header-top">
          <span className="proc-reference">{p.reference}</span>
          <StatusBadge status={p.status} label={p.status === 'in_progress' ? STAGE_LABELS[p.stage].toUpperCase() : STATUS_LABELS[p.status]} />
        </div>
        <h1 className="proc-title">{p.title}</h1>
        <div className="proc-context">
          {[p.project_name, p.programme_name, p.location_name, CATEGORY_LABELS[p.category], p.procurement_method ? METHOD_LABELS[p.procurement_method] : null].filter(Boolean).join(' · ') || 'Not linked to a project'}
        </div>
        <div className="proc-meta">
          <span>Estimated Value <strong>{formatMoney(p.estimated_value, p.currency_code)}</strong></span>
          <span>Procurement Officer <strong>{p.procurement_officer_name ?? 'Not assigned'}</strong></span>
          <span>Closing <strong>{p.closing_date ? formatDate(p.closing_date) : '-'}</strong></span>
          <span>Required By <strong>{p.required_by_date ? formatDate(p.required_by_date) : '-'}</strong></span>
        </div>
        <div className="proc-actions">
          {isDraft && canManage && <button onClick={() => submit.mutate()} disabled={submit.isPending}>Submit Request</button>}
          {isActive && canAdvance && <button onClick={() => setAdvanceOpen(true)}>{nextStageLabel}</button>}
          {!isTerminal && canAdvance && <button className="btn-secondary" onClick={() => setCancelOpen(true)}>Cancel Procurement</button>}
        </div>
      </div>

      <div className={'proc-lifecycle' + (p.status === 'cancelled' ? ' proc-lifecycle-cancelled' : '')} aria-label="Procurement lifecycle">
        {PROCUREMENT_STAGES.map((s, i) => {
          const done = p.status === 'completed' || i < currentIndex;
          const current = !done && i === currentIndex;
          return (
            <div key={s} className={'proc-stage' + (done ? ' proc-stage-done' : '') + (current ? ' proc-stage-current' : '')}>
              <span className="proc-stage-dot">{done ? <Check size={11} strokeWidth={3} /> : null}</span>
              <span className="proc-stage-label">{STAGE_LABELS[s]}</span>
            </div>
          );
        })}
        {p.status === 'cancelled' && <span className="proc-lifecycle-note">Cancelled at {STAGE_LABELS[p.stage]}</span>}
      </div>

      <nav className="workspace-tabs">
        {TABS.map((t) => {
          const c = countFor(t.key);
          return (
            <button key={t.key} className={'workspace-tab' + (activeTab === t.key ? ' workspace-tab-active' : '')} onClick={() => setActiveTab(t.key)}>
              {t.label}{c && c.total > 0 && <span className={'proc-tab-count' + (c.open > 0 ? ' proc-tab-count-open' : '')}>{c.total}</span>}
            </button>
          );
        })}
      </nav>

      {activeTab === 'overview' && (
        <>
          <div className="proc-control">
            <div><span className="proc-control-label">Current Stage</span><strong>{STAGE_LABELS[p.stage]}</strong></div>
            <div><span className="proc-control-label">Days in Stage</span><strong>{p.days_in_stage}</strong></div>
            <div><span className="proc-control-label">Next Required Action</span><strong>{nextAction(p)}</strong></div>
            <div><span className="proc-control-label">Key Deadline</span><strong>{p.closing_date ? `Closing ${formatDate(p.closing_date)}` : p.required_by_date ? `Required ${formatDate(p.required_by_date)}` : '-'}</strong></div>
          </div>
          {attention.length > 0 && (
            <section className="card">
              <h2>Attention Required</h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {attention.map((a) => <li key={a} className="warning-state">{a}</li>)}
              </ul>
            </section>
          )}
          <section className="card">
            <h2>Procurement Summary</h2>
            <dl className="proc-summary">
              <dt>Project</dt><dd>{p.project_name ? `${p.project_name} (${p.project_reference})` : '-'}</dd>
              <dt>Programme</dt><dd>{p.programme_name ?? '-'}</dd>
              <dt>Requester</dt><dd>{p.requested_by_name}</dd>
              <dt>Department</dt><dd>{p.department_name ?? '-'}</dd>
              <dt>Category</dt><dd>{CATEGORY_LABELS[p.category]}</dd>
              <dt>Method</dt><dd>{p.procurement_method ? METHOD_LABELS[p.procurement_method] : 'Not yet selected'}</dd>
              <dt>Estimated Value</dt><dd>{formatMoney(p.estimated_value, p.currency_code)}</dd>
              <dt>Line Items Total</dt><dd>{itemsTotal > 0 ? formatMoney(itemsTotal, p.currency_code) : '-'} <span className="muted">({p.item_count} item{Number(p.item_count) === 1 ? '' : 's'})</span></dd>
              <dt>Created</dt><dd>{formatDate(p.created_at)}</dd>
            </dl>
          </section>
          <section className="card">
            <h2>Recent Activity</h2>
            <HistoryList history={p.history.slice(-5).reverse()} />
          </section>
          <CommentsThread entityType="procurement" entityId={procurementId} section="overview" heading="Comments — Overview" />
        </>
      )}

      {activeTab === 'requisition' && (
        <>
          <section className="card">
            <h2>Request Information</h2>
            {isDraft && canManage ? (
              <form onSubmit={draftForm.handleSubmit((v) => saveDraft.mutate(v))}>
                <label>Title<input defaultValue={p.title} {...draftForm.register('title', { required: true })} /></label>
                <label>Description<textarea rows={3} defaultValue={p.description ?? ''} {...draftForm.register('description')} /></label>
                <label>Justification<textarea rows={3} defaultValue={p.justification ?? ''} {...draftForm.register('justification')} /></label>
                <label>Delivery Requirements<textarea rows={2} defaultValue={p.delivery_requirements ?? ''} {...draftForm.register('deliveryRequirements')} /></label>
                <div className="form-row">
                  <label>Estimated Value<input type="number" min="0" step="0.01" defaultValue={p.estimated_value ?? ''} {...draftForm.register('estimatedValue')} /></label>
                  <label>Required By<input type="date" defaultValue={p.required_by_date?.slice(0, 10) ?? ''} {...draftForm.register('requiredByDate')} /></label>
                </div>
                <div className="form-actions"><button type="submit" disabled={saveDraft.isPending}>Save Requisition</button></div>
              </form>
            ) : (
              <dl className="proc-summary">
                <dt>Description</dt><dd>{p.description ?? '-'}</dd>
                <dt>Justification</dt><dd>{p.justification ?? '-'}</dd>
                <dt>Delivery Requirements</dt><dd>{p.delivery_requirements ?? '-'}</dd>
                <dt>Required By</dt><dd>{p.required_by_date ? formatDate(p.required_by_date) : '-'}</dd>
              </dl>
            )}
            {!isDraft && <p className="muted" style={{ fontSize: 12 }}>The requisition is locked once submitted, so everyone downstream works from the same request.</p>}
          </section>

          <section className="card">
            <h2>Items / Services</h2>
            <DataTable
              rows={p.items}
              rowKey={(it) => it.id}
              emptyMessage="No line items yet."
              columns={[
                { header: '#', render: (it) => it.line_order },
                { header: 'Description', render: (it) => <>{it.description}{it.specification && <div className="muted" style={{ fontSize: 12 }}>{it.specification}</div>}</> },
                { header: 'Unit', render: (it) => it.unit },
                { header: 'Qty', render: (it) => Number(it.quantity).toLocaleString() },
                { header: 'Est. Unit Cost', render: (it) => formatMoney(it.estimated_unit_cost, p.currency_code) },
                { header: 'Est. Total', render: (it) => it.estimated_unit_cost ? formatMoney(Number(it.quantity) * Number(it.estimated_unit_cost), p.currency_code) : '-' },
                { header: '', render: (it) => isDraft && canManage ? <button className="link-button" onClick={() => removeItem.mutate(it.id)}>Remove</button> : null },
              ]}
            />
            {itemsTotal > 0 && <p style={{ textAlign: 'right', marginTop: 8 }}>Estimated total: <strong>{formatMoney(itemsTotal, p.currency_code)}</strong></p>}
            {isDraft && canManage && (
              <form className="inline-form" onSubmit={itemForm.handleSubmit((v) => addItem.mutate(v))} style={{ marginTop: 12 }}>
                <label style={{ flex: 2 }}>Description<input {...itemForm.register('description', { required: true })} /></label>
                <label>Unit<input placeholder="e.g. kit" {...itemForm.register('unit', { required: true })} /></label>
                <label>Qty<input type="number" min="0" step="any" {...itemForm.register('quantity', { required: true })} /></label>
                <label>Est. Unit Cost<input type="number" min="0" step="0.01" {...itemForm.register('estimatedUnitCost')} /></label>
                <label style={{ flex: 2 }}>Specification<input {...itemForm.register('specification')} /></label>
                <button type="submit" disabled={addItem.isPending}>Add Item</button>
              </form>
            )}
          </section>
          <CommentsThread entityType="procurement" entityId={procurementId} section="requisition" heading="Comments — Requisition" />
        </>
      )}

      {activeTab === 'sourcing' && (
        <>
          <section className="card">
            <h2>Procurement Method &amp; Schedule</h2>
            {isActive && canAdvance ? (
              <form onSubmit={assignForm.handleSubmit((v) => assign.mutate(v))}>
                <div className="form-row">
                  <label>Procurement Method
                    <select defaultValue={p.procurement_method ?? ''} {...assignForm.register('procurementMethod')}>
                      <option value="">Not yet selected</option>
                      {Object.entries(METHOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </label>
                  <label>Procurement Officer<UserPicker defaultValue={p.procurement_officer_id ?? ''} {...assignForm.register('procurementOfficerId')} /></label>
                </div>
                <label style={{ maxWidth: 260 }}>Closing Date<input type="date" defaultValue={p.closing_date?.slice(0, 10) ?? ''} {...assignForm.register('closingDate')} /></label>
                <div className="form-actions"><button type="submit" disabled={assign.isPending}>Save Sourcing Details</button></div>
              </form>
            ) : (
              <dl className="proc-summary">
                <dt>Method</dt><dd>{p.procurement_method ? METHOD_LABELS[p.procurement_method] : 'Not yet selected'}</dd>
                <dt>Procurement Officer</dt><dd>{p.procurement_officer_name ?? 'Not assigned'}</dd>
                <dt>Closing Date</dt><dd>{p.closing_date ? formatDate(p.closing_date) : '-'}</dd>
              </dl>
            )}
            {isDraft && <p className="muted" style={{ fontSize: 12 }}>Sourcing details can be set once the request is submitted.</p>}
            <p className="muted" style={{ fontSize: 12 }}>Invited vendors, solicitation documents, addenda and clarifications arrive with the Vendor Register phase.</p>
          </section>
          <CommentsThread entityType="procurement" entityId={procurementId} section="sourcing" heading="Comments — Sourcing" />
        </>
      )}

      {activeTab === 'documents' && (
        <>
          <DocumentsSection
            documents={docsQuery.data?.data ?? []}
            downloadUrl={(documentId) => genericDocumentApi.downloadUrl('procurement', documentId)}
            upload={(input) => genericDocumentApi.uploadFor('procurement', procurementId, input)}
            onDelete={(documentId) => genericDocumentApi.deleteFor('procurement', documentId)}
            queryKeyToInvalidate={['procurement-documents', procurementId]}
            hrCategories={false}
          />
          <CommentsThread entityType="procurement" entityId={procurementId} section="documents" heading="Comments — Documents" />
        </>
      )}

      {activeTab === 'activity' && (
        <section className="card">
          <h2>Stage History</h2>
          <p className="muted" style={{ fontSize: 12 }}>Every stage and status change on this procurement, oldest first. Discussion lives on each tab; this is the record of what actually happened.</p>
          <HistoryList history={p.history} />
        </section>
      )}

      {advanceOpen && (
        <Modal title={nextStageLabel} onClose={() => setAdvanceOpen(false)}>
          <p>{p.stage === 'closeout' ? 'This marks the procurement as completed.' : `Move this procurement from ${STAGE_LABELS[p.stage]} to ${STAGE_LABELS[PROCUREMENT_STAGES[currentIndex + 1]]}.`}</p>
          <label>Note (optional)<textarea rows={3} value={advanceNote} onChange={(e) => setAdvanceNote(e.target.value)} /></label>
          <div className="form-actions"><button onClick={() => advance.mutate()} disabled={advance.isPending}>Confirm</button></div>
        </Modal>
      )}
      {cancelOpen && (
        <Modal title="Cancel Procurement" onClose={() => setCancelOpen(false)}>
          <p>Cancelling stops this procurement at its current stage. It cannot be undone.</p>
          <label>Reason (required)<textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} /></label>
          <div className="form-actions"><button className="button-danger" onClick={() => cancel.mutate()} disabled={cancel.isPending || cancelReason.trim().length < 5}>Cancel Procurement</button></div>
        </Modal>
      )}
    </div>
  );
}

function HistoryList({ history }: { history: ProcurementDetail['history'] }) {
  if (history.length === 0) return <p className="muted">No activity yet.</p>;
  return (
    <ul className="proc-history">
      {history.map((h) => (
        <li key={h.id}>
          <div><strong>{h.from_stage && h.from_stage !== h.to_stage ? `${STAGE_LABELS[h.from_stage]} → ${STAGE_LABELS[h.to_stage]}` : STATUS_LABELS[h.to_status] ?? h.to_status}</strong>
            <span className="muted"> · {h.changed_by_name} · {new Date(h.changed_at).toLocaleString()}</span></div>
          {h.note && <div className="muted">{h.note}</div>}
        </li>
      ))}
    </ul>
  );
}
